/**
 *  This hook is largely copied from useConvertToTypeaheadResults
 *  in order to support the new omnibox result types we wanted to include all existing functionality
 *  in a separate hook rather than overloading the existing hook and adding additional complexity
 */
import { ContentIcon } from "@dropbox/dash-component-library";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CalculatorLine,
  ClockLine,
  CloseLine,
  EditLine,
  LinkLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { PAP_Click_JoinMeeting } from "@mirage/analytics/events/types/click_join_meeting";
import { PAP_Open_CalendarDetails } from "@mirage/analytics/events/types/open_calendar_details";
import { ChatEntryPoint } from "@mirage/conversations/types";
import { DashStackIcon } from "@mirage/mosaics/DCLWrappers/DashStackIcon";
import { useStacksNavigation } from "@mirage/mosaics/Omnibox/hooks/useStacksNavigation";
import { EnterKey } from "@mirage/mosaics/Results/EnterKey";
import { ResultAction } from "@mirage/mosaics/Results/ResultActions";
import { getTypeaheadFilterDisplayProps } from "@mirage/mosaics/SearchBarWithTypeahead/typeahead-search-filter-result";
import { type TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";
import { SummarizeAction } from "@mirage/mosaics/SummaryQnaPanel/SummarizeAction";
import { SummaryQnAEnum } from "@mirage/mosaics/SummaryQnaPanel/utils";
import { getUserProfilePageHref } from "@mirage/mosaics/UserProfilePage/utils";
import { Highlighter } from "@mirage/search/SearchResults/Highlighter";
import { StackItemSubtitle } from "@mirage/search/SearchResults/StackItemTitle";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { useDualMode } from "@mirage/service-experimentation/useDualMode";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { tagged } from "@mirage/service-logging";
import { useOnboardingValues } from "@mirage/service-onboarding/hooks";
import {
  copyToClipboard,
  openLocalPath,
  openURL,
  runLocalLaunchCommandForShortcut,
} from "@mirage/service-platform-actions";
import { reportPapEvent } from "@mirage/service-product-logging";
import { openResult } from "@mirage/service-result-actions";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import useSettings from "@mirage/service-settings/useSettings";
import { stackGetShareId } from "@mirage/service-stacks/service/utils";
import {
  addURLShortcutToCache,
  registerHit,
  trackSectionInteraction,
} from "@mirage/service-typeahead-search";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import { useDashActionSurface } from "@mirage/shared/hooks/useDashActionSurface";
import { calculatePapPositions } from "@mirage/shared/hooks/useResultPAPLogger";
import { getKeyMaps } from "@mirage/shared/hotkeys/hotkeysKeyMap";
import { UrlSrcIcon } from "@mirage/shared/icons";
import DesktopApplicationIcon from "@mirage/shared/icons/DesktopApplicationIcon";
import { FileContentIcon } from "@mirage/shared/icons/FileContentIcon";
import { IconButtonWithTooltip } from "@mirage/shared/icons/IconButtonWithTooltip";
import { PrettyShortcuts } from "@mirage/shared/keyboard-shortcuts/PrettyShortcuts";
import {
  getFilterBinding,
  mergePartialFilterText,
  SearchFilterType,
} from "@mirage/shared/search/search-filters";
import {
  CREATE_STACK_ACTION_UUID,
  PEOPLE_SHORTCUT_UUID,
  type WellKnownShortcutUUID,
} from "@mirage/shared/search/url-shortcut";
import { showSnackbar } from "@mirage/shared/snackbar";
import { faviconSrcForSrcUrl } from "@mirage/shared/util/favicon";
import i18n from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getDisplayResult } from "./getDisplayResult";

import type { LaunchMethod } from "@mirage/analytics/events/enums/launch_method";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { urlForConversationWithServerID } from "@mirage/mosaics/Chat/utils/navigation";
import {
  CtaSource,
  type WriteFromTemplateParams,
  type WriteFromToneParams,
  type WriteWithDocumentParams,
} from "@mirage/service-conversation/router_state_types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import type { useTypeaheadSearch } from "@mirage/service-typeahead-search/hooks/useTypeaheadSearch";
import { getStackIconUrl } from "@mirage/stacks/utils";

const logger = tagged("useConvertToTypeaheadResults");

type UseTypeaheadSearch = ReturnType<typeof useTypeaheadSearch>;

export type OmniboxTypeaheadResult = TypeaheadResult & {
  // Omnibox specific props
  sectionId?: string;
};

// IMPROVEMENT (AYC): consider moving this into hooks/ folder
export function useConvertToOmniboxResults({
  typeaheadQuery,
  setTypeaheadQuery,
  shouldSearchBeBoosted: _shouldSearchBeBoosted,
  handleTaggedSearchSubmit,
  removePreviousQuery,
  onInteractedWithResult,
  papLogResultOpen,
  papLogResultCopy,
  papTrackOmniboxFocusMode,
}: {
  typeaheadQuery: string;
  setTypeaheadQuery: (typeaheadQuery: string) => void;
  shouldSearchBeBoosted: boolean;
  handleTaggedSearchSubmit: (
    result:
      | typeahead.ScoredPreviousQuery
      | typeahead.ScoredSuggestedQuery
      | typeahead.ScoredPeopleFilter,
    isDefaultSearchDash: boolean,
  ) => void;
  removePreviousQuery: (result: typeahead.ScoredPreviousQuery) => void;
  /** Fired for either a copy or open on everything but "search dash" (SuggestedQuery) */
  onInteractedWithResult: (result: typeahead.ScoredResult) => void;
  papLogResultOpen: UseTypeaheadSearch["papLogResultOpen"];
  papLogResultCopy: UseTypeaheadSearch["papLogResultCopy"];
  papTrackOmniboxFocusMode: () => void;
}) {
  const platform = EnvCtx.platform as "darwin" | "win32";
  const isDesktop = EnvCtx.surface === "desktop";
  const isDarkMode = useTheme();
  const navigate = useNavigate();
  const { setOnboardingValue } = useOnboardingValues();
  const { isDualModeEnabled, isDualModeLauncher } = useDualMode();
  const { navigateToStacks } = useStacksNavigation();

  const { dashActionSurface } = useDashActionSurface();
  const isSERP = dashActionSurface === "serp";
  const isInStack =
    dashActionSurface === "stack_details_page" ||
    dashActionSurface === "new_stack_page";

  const enableTypeaheadCSummarizableRowButton =
    useFeatureFlagValue("dash_2024_09_19_typeahead_summary_row_button") ===
    "ON";
  const isOmniboxEnabled = convertFeatureValueToBool(
    useFeatureFlagValue("dash_2025_03_18_omnibox"),
  );
  const openFilesInDesktopApps = !!useSettings(["openFilesInDesktopAppsV2"])
    .settings?.openFilesInDesktopAppsV2;

  return useCallback(
    (searchResults: typeahead.ScoredResult[]): OmniboxTypeaheadResult[] =>
      searchResults
        .map((result) => {
          const baseResult = {
            type: result.type,
            uuid: result.uuid,
            scoredResult: result,
            sectionId: result.sectionId,
          } satisfies Partial<OmniboxTypeaheadResult>;

          const handleTrackSectionInteraction = (timestamp: number) => {
            if (!isOmniboxEnabled) return;

            trackSectionInteraction(result.sectionId, {
              timestamp,
              sectionScore: result.sectionScore,
              resultUuid: result.uuid,
            });
          };

          switch (result.type) {
            case typeahead.ResultType.DesktopFile: {
              return {
                ...baseResult,
                icon: <FileContentIcon content={result.result} />,
                title: result.result.title,
                subtitle: i18n.t("search_result_recent_file"),
                launchResult: (launchMethod: LaunchMethod) => {
                  if (!result.result.url) return;
                  handleTrackSectionInteraction(Date.now());
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  registerHit(result.uuid);
                  onInteractedWithResult(result);
                  openLocalPath(result.result.url);
                  if (!isSERP) {
                    setTypeaheadQuery("");
                    if (!isDualModeLauncher) navigate("/");
                  }
                },
              };
            }
            case typeahead.ResultType.DesktopApplication:
              return {
                ...baseResult,
                icon: (
                  <DesktopApplicationIcon result={result.result} size={32} />
                ),
                title: result.result.title,
                subtitle: i18n.t("desktop_application_subtitle"),
                launchResult: (launchMethod: LaunchMethod) => {
                  if (!result.result.url) return;
                  handleTrackSectionInteraction(Date.now());
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  registerHit(result.uuid);
                  onInteractedWithResult(result);
                  openLocalPath(result.result.url);
                  if (!isSERP) {
                    setTypeaheadQuery("");
                    if (!isDualModeLauncher) navigate("/");
                  }
                },
              };
            case typeahead.ResultType.Stack:
              return {
                ...baseResult,
                icon: (
                  <DashStackIcon
                    colorIndex={result.result.stack_data?.color_index}
                    emoji={result.result.stack_data?.emoji}
                    size="medium"
                    customColor={result.result.stack_data?.custom_color}
                    iconUrl={getStackIconUrl(result.result)}
                  />
                ),
                title:
                  result.result.stack_data?.name ||
                  i18n.t("stacks_typeahead_default_title"),
                subtitle: i18n.t("stacks_typeahead_subtitle"),
                launchResult: (launchMethod: LaunchMethod) => {
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  onInteractedWithResult(result);
                  navigate(`/stacks/${stackGetShareId(result.result) ?? ""}`);
                  if (!isSERP) {
                    setTypeaheadQuery("");
                  }
                },
                copyResult: () => {
                  if (!result.result.sharing_data?.shared_link?.url) return;
                  copyToClipboard(result.result.sharing_data.shared_link.url);
                  showSnackbar({ title: i18n.t("copied_link_to_clipboard") });
                  papLogResultCopy(result);
                  onInteractedWithResult(result);
                },
              };
            case typeahead.ResultType.StackItem:
              return {
                ...baseResult,
                icon: (
                  <ContentIcon
                    icon={
                      result.result.url ? (
                        <UrlSrcIcon
                          name={i18n.t("icon_for_item")}
                          size="100%"
                          iconUrl={faviconSrcForSrcUrl(
                            result.result.url,
                            32,
                            isDarkMode,
                          )}
                        />
                      ) : (
                        <UIIcon src={LinkLine} />
                      )
                    }
                    size="medium"
                  />
                ),
                title: result.result.custom_name || result.result.name,
                subtitle: <StackItemSubtitle stackItem={result.result} />,
                launchResult: (launchMethod: LaunchMethod) => {
                  if (!result.result.url) return;
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  registerHit(result.uuid);
                  onInteractedWithResult(result);
                  openURL(result.result.url);
                  if (!isSERP && !isInStack) {
                    setTypeaheadQuery("");
                    if (!isDualModeLauncher) navigate("/");
                  }
                },
                copyResult: () => {
                  if (!result.result.url) return;
                  copyToClipboard(result.result.url);
                  showSnackbar({ title: i18n.t("copied_link_to_clipboard") });
                  papLogResultCopy(result);
                  onInteractedWithResult(result);
                },
              };
            case typeahead.ResultType.CalendarEvent: {
              const conferenceURL = result.result.conferenceLinks[0]?.link;

              const summaryResult = {
                ...baseResult,
                ...getDisplayResult(typeaheadQuery, result),
                summarizable: result?.result?.summarizable,
                launchResult: (launchMethod: LaunchMethod) => {
                  if (!result.result.url && !conferenceURL) return;
                  openURL(conferenceURL || result.result.url!);
                  reportPapEvent(
                    conferenceURL
                      ? PAP_Click_JoinMeeting({
                          meetingId: result.uuid,
                          featureLine: "calendar",
                        })
                      : PAP_Open_CalendarDetails({
                          meetingId: result.uuid,
                          featureLine: "calendar",
                        }),
                  );
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  registerHit(result.uuid);
                  onInteractedWithResult(result);
                },
                copyResult: () => {
                  if (!result.result.url) return;
                  copyToClipboard(result.result.url);
                  showSnackbar({ title: i18n.t("copied_link_to_clipboard") });
                  papLogResultCopy(result);
                  onInteractedWithResult(result);
                },
              };

              const accessory: TypeaheadResult["accessory"] = (
                <ResultAction
                  action={{
                    key: "join",
                    title: conferenceURL
                      ? i18n.t("join_meeting_short")
                      : i18n.t("result_open_button_text"),
                    onClick: summaryResult.launchResult,
                    icon: <EnterKey />,
                    hidden: !conferenceURL && !result.result.url,
                  }}
                />
              );
              return { ...summaryResult, accessory };
            }

            case typeahead.ResultType.SearchResult:
            case typeahead.ResultType.Recommendation: {
              const summaryResult = {
                ...baseResult,
                ...getDisplayResult(typeaheadQuery, result),
                summarizable: result?.result?.summarizable,
                launchResult: (launchMethod: LaunchMethod) => {
                  if (!result.result.url) return;
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  registerHit(result.uuid);
                  onInteractedWithResult(result);
                  openResult(result.result).catch((e: Error) => {
                    logger.error("Failed to open result", e);
                  });
                  if (!isSERP && !isInStack) {
                    setTypeaheadQuery("");
                    if (!isDualModeLauncher) navigate("/");
                  }
                },
                copyResult: () => {
                  if (!result.result.url) return;
                  copyToClipboard(result.result.url);
                  showSnackbar({ title: i18n.t("copied_link_to_clipboard") });
                  papLogResultCopy(result);
                  onInteractedWithResult(result);
                },
              };

              const { resultPosition, resultPositionNoCta } =
                calculatePapPositions(
                  searchResults,
                  (r) => r.uuid === result.uuid,
                );

              let accessory: TypeaheadResult["accessory"] = null;
              if (
                enableTypeaheadCSummarizableRowButton &&
                summaryResult.summarizable === "yes_summarizable"
              ) {
                accessory = (
                  <SummarizeAction
                    result={summaryResult}
                    summaryQnAType={SummaryQnAEnum.TYPEAHEAD}
                    entrypoint={ChatEntryPoint.search_bar_inline}
                    resultPosition={resultPosition}
                    resultPositionNoCta={resultPositionNoCta}
                    summarizable={summaryResult.summarizable || undefined}
                  />
                );
              }

              return { ...summaryResult, accessory };
            }
            case typeahead.ResultType.MyProfileSuggestion:
            case typeahead.ResultType.PeopleRecommendation: {
              return {
                ...baseResult,
                ...getDisplayResult(typeaheadQuery, result),
                launchResult: (launchMethod: LaunchMethod) => {
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  registerHit(result.result.uuid);
                  onInteractedWithResult(result);
                  const profileHref = result.result.displayHandle
                    ? getUserProfilePageHref(result.result.displayHandle)
                    : undefined;
                  if (profileHref) {
                    navigate(profileHref, { state: { source: "omnibox" } });
                  }
                  if (!isSERP) {
                    setTypeaheadQuery("");
                  }
                },
              };
            }
            case typeahead.ResultType.PreviousQuery: {
              const accessory = (
                <IconButtonWithTooltip
                  aria-label={i18n.t("aria_remove_recent_result")}
                  variant="transparent"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    removePreviousQuery(result);
                  }}
                  tooltipProps={{
                    title: i18n.t("tooltip_remove_recent_result"),
                  }}
                >
                  <UIIcon src={CloseLine} />
                </IconButtonWithTooltip>
              );
              return {
                ...baseResult,
                icon: <UIIcon src={ClockLine} />,
                title: result.result.query,
                subtitle: "",
                launchResult: (launchMethod: LaunchMethod) => {
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  onInteractedWithResult(result);
                  handleTaggedSearchSubmit(result, false);
                },
                removeResult: () => {
                  removePreviousQuery(result);
                },
                hoverAccessory: accessory,
              };
            }
            // NOTE: we need to separate out the case where a suggested query can either be
            // search suggestion OR AI/Chat suggestion
            case typeahead.ResultType.SuggestedQuery:
            case typeahead.ResultType.AskUserQuery:
            case typeahead.ResultType.WriteUserQuery:
              return {
                ...baseResult,
                ...getDisplayResult(typeaheadQuery, result),
                launchResult: (launchMethod: LaunchMethod) => {
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();

                  if (result.type === typeahead.ResultType.AskUserQuery) {
                    const conversationRouterState =
                      buildConversationRouterState(CtaSource.OmniboxAskQuery, {
                        message: result.result.query,
                      });
                    navigate(RoutePath.CHAT, {
                      state: conversationRouterState,
                    });
                    return;
                  }

                  if (result.type === typeahead.ResultType.WriteUserQuery) {
                    navigate(RoutePath.CHAT, {
                      state: buildConversationRouterState(
                        CtaSource.OmniboxDraftWithMessage,
                        { message: result.result.query },
                      ),
                    });
                    return;
                  }

                  handleTaggedSearchSubmit(result, false);
                },
                accessory: (
                  <PrettyShortcuts
                    shortcut={
                      getKeyMaps({ platform, isDesktop, isDualModeEnabled })
                        .typeahead.searchQuery
                    }
                    isDarwin={platform === "darwin"}
                  />
                ),
                selectedAccessory: <></>, // Don't show anything when selected
                copyResult: () => {
                  if (!result.result.query?.length) return;
                  copyToClipboard(result.result.query);
                },
              };
            case typeahead.ResultType.OrganizeUserQuery:
              return {
                ...baseResult,
                // NOTE: placeholder value and not used (here because of TS definitions)
                icon: <UIIcon src={LinkLine} />, // NOTE: this is a placeholder icon
                subtitle: "",
                title: "",
                // Note: moved out previous icon, title, subtitle logic out to custom result row
                launchResult: (launchMethod: LaunchMethod) => {
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  navigateToStacks(result.result.query, "manual");
                },
              };
            case typeahead.ResultType.URLShortcut:
              return {
                ...baseResult,
                // NOTE: moved out previous icon, title, subtitle logic out to custom result row
                // could not update the types without a major refactor to allow for optional icon, title, subtitle values
                icon: <UIIcon src={LinkLine} />, // NOTE: this is a placeholder icon
                title: "", // NOTE: placeholder value
                // Note: moved out previous icon, title, subtitle logic out to custom result row
                launchResult: async (launchMethod: LaunchMethod) => {
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  addURLShortcutToCache(result.result);

                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  onInteractedWithResult(result);

                  if (result.uuid === CREATE_STACK_ACTION_UUID) {
                    papLogResultOpen(result, launchMethod);
                    papTrackOmniboxFocusMode();
                    onInteractedWithResult(result);
                    // create stack
                    navigate("/stacks/new");
                    setTypeaheadQuery("");
                    return;
                  }

                  if (result.uuid === PEOPLE_SHORTCUT_UUID) {
                    // Navigate to relative path for Dash People shortcut
                    const url = new URL(result.result.parameters.url);
                    navigate(url.pathname);
                    setTypeaheadQuery("");
                    return;
                  }

                  if (!result.result.parameters.url?.length) {
                    logger.warn(
                      "attempted to launch URLShortcut without a URL, ignoring",
                    );
                    return;
                  }

                  if (!isSERP && !isInStack) {
                    setTypeaheadQuery("");
                    if (!isDualModeLauncher) navigate("/");
                  }

                  try {
                    let launchedLocally = false;
                    if (openFilesInDesktopApps) {
                      launchedLocally = await runLocalLaunchCommandForShortcut(
                        result.uuid as WellKnownShortcutUUID,
                      );
                    }

                    if (!launchedLocally) {
                      await openURL(result.result.parameters.url);
                    }
                  } catch (e) {
                    logger.error("Failed to launch URLShortcut", e);
                  }
                },
                copyResult: () => {
                  if (!result.result.parameters.url) return;
                  copyToClipboard(result.result.parameters.url);
                  papLogResultCopy(result);
                  onInteractedWithResult(result);
                  showSnackbar({ title: i18n.t("copied_link_to_clipboard") });
                },
                // Give this result priority over others. We want URL shortcuts appearing 1st in result list
                priority: 1,
              };
            case typeahead.ResultType.MathCalculation: {
              const copyResultCallback = () => {
                copyToClipboard(result.result.answer);
                showSnackbar({
                  title: i18n.t("copied_math_to_clipboard"),
                });
                papLogResultCopy(result);
                onInteractedWithResult(result);
              };

              return {
                ...baseResult,
                icon: <UIIcon src={CalculatorLine} />,
                title: (
                  <Highlighter
                    title={`${typeaheadQuery.trim().replace(/\s+/g, " ")} = ${
                      result.result.answer
                    }`}
                    highlightWords={[`= ${result.result.answer}`]}
                  />
                ),
                subtitle: i18n.t("math_calculation_byline"),
                launchResult: () => {
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  copyResultCallback();
                  copyResultCallback();
                  papTrackOmniboxFocusMode();
                  setTypeaheadQuery(result.result.answer);
                },
                copyResult: copyResultCallback,
                // Give this result priority over others. We want math appearing 1st in result list
                priority: 1,
              };
            }
            case typeahead.ResultType.SearchFilter: {
              return {
                ...baseResult,
                ...getTypeaheadFilterDisplayProps(
                  typeaheadQuery,
                  result.result,
                ),
                launchResult: (launchMethod: LaunchMethod) => {
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  const filterBinding = getFilterBinding(result.result);
                  const mergedFilterText = mergePartialFilterText(
                    typeaheadQuery,
                    filterBinding,
                  );
                  setTypeaheadQuery(mergedFilterText + " ");
                },
                keepTypeaheadOpen: true,
                priority: 1,
              };
            }

            // NOTE: Requires completion of integration
            case typeahead.ResultType.AskSuggestion: {
              return {
                ...baseResult,
                ...getDisplayResult(typeaheadQuery, result),
                launchResult: (launchMethod: LaunchMethod) => {
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  const routerState = buildConversationRouterState(
                    CtaSource.OmniboxAskFromSuggestion,
                    {
                      message: result.result.text,
                      attachments: result.result.docIds || [],
                      agentIds: result.result.agentIds || [],
                    },
                  );
                  navigate(RoutePath.CHAT, {
                    state: routerState,
                  });
                },
              };
            }

            // NOTE: Requires completion of integration
            case typeahead.ResultType.RecentChat: {
              return {
                ...baseResult,
                ...getDisplayResult(typeaheadQuery, result),
                launchResult: (launchMethod: LaunchMethod) => {
                  setOnboardingValue("dismissedQuerySuggestionBanner", false);
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  const url = urlForConversationWithServerID(
                    result.result.serverConversationId,
                  );
                  navigate(url, {
                    state: buildConversationRouterState(
                      CtaSource.OmniboxRecentChat,
                      {},
                    ),
                  });
                },
              };
            }
            case typeahead.ResultType.DraftWithSource: {
              return {
                ...baseResult,
                // NOTE: these values are passed through and not used
                // Improvement - need to remove this from the type
                icon: <UIIcon src={EditLine} />,
                title: result.result.title,
                subtitle: "",
                launchResult: (launchMethod: LaunchMethod) => {
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  const source: ChatSource = {
                    type: "recommendation",
                    recommendation: result.result,
                  };
                  const routerState = buildConversationRouterState(
                    CtaSource.OmniboxDraftWithDocument,
                    {
                      chatSources: [source],
                    } satisfies WriteWithDocumentParams,
                  );
                  navigate(RoutePath.CHAT, {
                    state: routerState,
                  });
                },
              };
            }
            case typeahead.ResultType.DraftFromTemplate: {
              return {
                ...baseResult,
                icon: <UIIcon src={EditLine} />,
                title: result.result.title,
                subtitle: "",
                launchResult: (launchMethod: LaunchMethod) => {
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  const routerState = buildConversationRouterState(
                    CtaSource.OmniboxDraftWithTemplate,
                    {
                      templateId: result.result.templateId,
                    } satisfies WriteFromTemplateParams,
                  );
                  navigate(RoutePath.CHAT, {
                    state: routerState,
                  });
                },
              };
            }
            case typeahead.ResultType.DraftFromTone: {
              return {
                ...baseResult,
                icon: <UIIcon src={EditLine} />,
                title: result.result.title,
                subtitle: "",
                launchResult: (launchMethod: LaunchMethod) => {
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  const routerState = buildConversationRouterState(
                    CtaSource.OmniboxDraftWithTone,
                    {
                      voiceId: result.result.voiceId,
                    } satisfies WriteFromToneParams,
                  );
                  navigate(RoutePath.CHAT, {
                    state: routerState,
                  });
                },
              };
            }
            case typeahead.ResultType.PeopleFilter: {
              return {
                ...baseResult,
                ...getTypeaheadFilterDisplayProps(typeaheadQuery, {
                  type: SearchFilterType.Person,
                  parameters: result.result,
                  id: result.result.uuid,
                }),
                launchResult: (launchMethod: LaunchMethod) => {
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                  registerHit(result.result.uuid);
                  handleTaggedSearchSubmit(result, false);
                },
              };
            }
            case typeahead.ResultType.Skeleton: {
              return {
                ...baseResult,
                ...getDisplayResult(typeaheadQuery, result),
                launchResult: (launchMethod: LaunchMethod) => {
                  handleTrackSectionInteraction(Date.now());
                  papLogResultOpen(result, launchMethod);
                  papTrackOmniboxFocusMode();
                },
              };
            }
            default:
              logger.warn("Unrecognized typeahead result type");
              return undefined;
          }
        })
        .sort(handleSortByPriority)
        .filter((result) => !!result),
    [
      enableTypeaheadCSummarizableRowButton,
      handleTaggedSearchSubmit,
      isDarkMode,
      isDesktop,
      isDualModeEnabled,
      isDualModeLauncher,
      isInStack,
      isOmniboxEnabled,
      isSERP,
      navigate,
      navigateToStacks,
      onInteractedWithResult,
      openFilesInDesktopApps,
      papLogResultCopy,
      papLogResultOpen,
      papTrackOmniboxFocusMode,
      platform,
      removePreviousQuery,
      setOnboardingValue,
      setTypeaheadQuery,
      typeaheadQuery,
    ],
  );
}

const handleSortByPriority = (a?: TypeaheadResult, b?: TypeaheadResult) => {
  // Assign 0 as default if 'priority' is undefined
  const aPriority = a?.priority ?? 0;
  const bPriority = b?.priority ?? 0;

  return bPriority - aPriority;
};
